import Modal from '@/components/common/Modal';
import Spacer from '@/components/common/Spacer';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';
import styled from '@emotion/styled';
import { Theme } from '@/styles/Theme';

interface Props {
  setIsShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedWorkers: number[];
  onSelectDisableAutoRenew: () => void;
  onSelectDeleteWorker: () => void;
  onSelectUndoUnsubscribe?: () => void;
  workerStatus?: '활성' | '비활성 예정';
  isLastActiveWorker?: boolean;
  subscriptionStatus?: 'free' | 'active' | 'expired';
  activeWorkerCount?: number;
}

const SelectWorkerActionModal = ({
  setIsShowModal,
  selectedWorkers,
  onSelectDisableAutoRenew,
  onSelectDeleteWorker,
  onSelectUndoUnsubscribe,
  workerStatus = '활성',
  isLastActiveWorker = false,
  subscriptionStatus = 'free',
  activeWorkerCount = 0,
}: Props) => {
  const isScheduledDeactivation = workerStatus === '비활성 예정';
  const isPaidUser = subscriptionStatus === 'active' || subscriptionStatus === 'expired';
  
  // Check if deleting all selected workers would leave no active workers
  // Only check for active workers (not scheduled deactivation)
  const wouldDeleteAllWorkers = !isScheduledDeactivation && 
    activeWorkerCount > 0 && 
    selectedWorkers.length >= activeWorkerCount;

  return (
    <Modal
      setIsModalOpen={setIsShowModal}
      title={isScheduledDeactivation ? '작업자 관리 방법 선택' : '작업자 중단 방법 선택'}
      width='31.25rem'
      hasButton={false}
    >
      <Spacer y='1.875rem' />
      <Text fontSize='1.125rem' fontWeight={500} textAlign='center'>
        선택한 작업자({selectedWorkers.length}명)에 대해 {isScheduledDeactivation ? '관리 방법' : '중단 방법'}을 선택해주세요.
      </Text>
      <Spacer y='2.5rem' />
      <ActionButtonContainer>
        {isScheduledDeactivation ? (
          <>
            <ActionButton onClick={onSelectUndoUnsubscribe}>
              <ActionButtonTitle>중단 취소</ActionButtonTitle>
              <ActionButtonDescription>
                작업자는 계속 사용할 수 있으며,
                <br />
                다음 결제일 자동 결제 예정입니다.
              </ActionButtonDescription>
            </ActionButton>
            <ActionButton onClick={onSelectDeleteWorker} isDelete>
              <ActionButtonTitle>작업자 삭제</ActionButtonTitle>
              <ActionButtonDescription>
                작업자는 즉시 로그인할 수 없습니다.
                <br />
                (복구 가능)
              </ActionButtonDescription>
            </ActionButton>
          </>
        ) : (
          <>
            {isPaidUser && (
              <ActionButton 
                onClick={onSelectDisableAutoRenew}
                disabled={isLastActiveWorker || wouldDeleteAllWorkers}
                isDisabled={isLastActiveWorker || wouldDeleteAllWorkers}
              >
                <ActionButtonTitle>자동 결제 해제</ActionButtonTitle>
                <ActionButtonDescription>
                  {isLastActiveWorker || wouldDeleteAllWorkers
                    ? '최소 1명의 작업자는 자동 결제를 유지해야 합니다.'
                    : (
                      <>
                        작업자는 계속 사용할 수 있지만,
                        <br />
                        다음 달 자동 결제에서 제외됩니다.
                      </>
                    )}
                </ActionButtonDescription>
              </ActionButton>
            )}
            <ActionButton 
              onClick={onSelectDeleteWorker} 
              isDelete
              disabled={isLastActiveWorker || wouldDeleteAllWorkers}
              isDisabled={isLastActiveWorker || wouldDeleteAllWorkers}
            >
              <ActionButtonTitle>작업자 삭제</ActionButtonTitle>
              <ActionButtonDescription>
                {isLastActiveWorker || wouldDeleteAllWorkers
                  ? '최소 1명의 작업자는 유지해야 합니다.'
                  : (
                    <>
                      작업자는 즉시 로그인할 수 없습니다.
                      <br />
                      (복구 가능)
                    </>
                  )}
              </ActionButtonDescription>
            </ActionButton>
          </>
        )}
      </ActionButtonContainer>
      <Spacer y='1.875rem' />
      <Button
        width='100%'
        height='3.125rem'
        backgroundColor={Theme.colors.G_5}
        color={Theme.colors.BLACK}
        onClick={() => setIsShowModal(false)}
      >
        취소
      </Button>
    </Modal>
  );
};

export default SelectWorkerActionModal;

const ActionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const ActionButton = styled.button<{ isDelete?: boolean; isDisabled?: boolean }>`
  width: 100%;
  padding: 1.25rem;
  border: 0.0625rem solid
    ${({ isDelete, isDisabled }) => 
      isDisabled 
        ? Theme.colors.G_4
        : (isDelete ? Theme.colors.WARNING_RED : Theme.colors.G_3)};
  border-radius: 0.625rem;
  background-color: ${({ isDisabled }) => 
    isDisabled ? Theme.colors.G_6 : Theme.colors.WHITE};
  text-align: left;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.6 : 1)};

  &:hover {
    background-color: ${({ isDelete, isDisabled }) =>
      isDisabled 
        ? Theme.colors.G_6
        : (isDelete ? '#FFF5F5' : Theme.colors.G_6)};
    border-color: ${({ isDelete, isDisabled }) =>
      isDisabled
        ? Theme.colors.G_4
        : (isDelete ? Theme.colors.WARNING_RED : Theme.colors.B_3)};
  }
`;

const ActionButtonTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${Theme.colors.BLACK};
  margin-bottom: 0.5rem;
`;

const ActionButtonDescription = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${Theme.colors.G_1};
  line-height: 1.5;
`;

